/**
 * Classifies a thrown CRM read error (A7Y-4981).
 *
 * Outside production every `/admin/crm` route answers 409 with
 * `{ code: 'CRM_UNAVAILABLE' }`. The shared API client throws
 * `Request to <url> failed: <status> <statusText> - <json body>` on a non-2xx,
 * so the status and the body's code are read back from that message. Both must
 * match: a 409 with another code (a write conflict) is a real error, not "this
 * environment has no CRM".
 */
import { CRM_ERROR_CODES } from '@a7y/types';

export function isCrmUnavailableError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return /\bfailed: 409\b/.test(err.message) && err.message.includes(CRM_ERROR_CODES.unavailable);
}

function hasCode(err: unknown, status: number, code: string): boolean {
  if (!(err instanceof Error)) return false;
  return new RegExp(`\\bfailed: ${status}\\b`).test(err.message) && err.message.includes(code);
}

/**
 * A write the API refused because the record moved under it (A7Y-4983): a
 * rename since the page loaded, an item someone else already closed, a reused
 * request id. The page reloads the record and keeps the user's unsaved text.
 */
export function isCrmConflictError(err: unknown): boolean {
  return hasCode(err, 409, CRM_ERROR_CODES.conflict);
}

export function isCrmNotFoundError(err: unknown): boolean {
  return hasCode(err, 404, CRM_ERROR_CODES.notFound);
}

/** The API's own words for a failed CRM call: its `message`, then any field `errors`. */
export function crmErrorMessage(err: unknown): string {
  if (!(err instanceof Error)) return String(err);
  const body = /- (\{.*\})\s*$/s.exec(err.message);
  if (body) {
    try {
      const parsed = JSON.parse(body[1]) as { message?: unknown; errors?: unknown };
      const errors = Array.isArray(parsed.errors) ? parsed.errors.map(String) : [];
      if (typeof parsed.message === 'string')
        return errors.length ? `${parsed.message}: ${errors.join('; ')}` : parsed.message;
    } catch {
      // Not the API's JSON: fall through to the raw message.
    }
  }
  return err.message;
}
